import React from 'react';
import { useTranslation } from 'react-i18next';
import AsyncPaginatedSelectMC from '../../../../components/AsyncPaginatedSelectMC';
import { isPlatformOnMsRollout } from '../../../Integrations/platformChannelIntegration';

// Clave de `PlatformsOptions` (option.label.toLowerCase(), viene de MiaPlatform.name:
// "Whatsapp"/"Messenger"/"Instagram"/"Tiktok") → enums del MS que agrupa.
export const PLATFORM_GROUPS = {
  whatsapp: ['whatsapp_bsp_messages', 'whatsapp_qr_messages', 'whatsapp_gupshup_messages'],
  messenger: ['facebook_messages'],
  instagram: ['instagram_messages'],
  tiktok: ['tik_tok_messages']
};

export const ALL_CHAT_MS_PLATFORMS = Object.values(PLATFORM_GROUPS).flat();

export const buildAccountKey = (platform, account) => `${platform}:${account.id}`;

/**
 * Cuentas conectadas (platform_retailers) para un grupo de plataformas MS, filtradas
 * por el flag de rollout `inbox_microservice_*_retailers` de cada una.
 */
const flattenAccounts = (msPlatforms, integrationsStatus, retailerInfo) => {
  if (!integrationsStatus) return [];

  return msPlatforms
    .filter((platform) => isPlatformOnMsRollout(platform, retailerInfo))
    .flatMap((platform) => {
      const accounts = integrationsStatus[`${platform}_accounts`];
      if (!Array.isArray(accounts)) return [];

      return accounts.map((account) => ({ platform, ...account }));
    });
};

export const flattenAccountsForLabel = (platformLabel, integrationsStatus, retailerInfo) => (
  flattenAccounts(PLATFORM_GROUPS[platformLabel] || [], integrationsStatus, retailerInfo)
);

export const flattenAllChatAccounts = (integrationsStatus, retailerInfo) => (
  flattenAccounts(ALL_CHAT_MS_PLATFORMS, integrationsStatus, retailerInfo)
);

/**
 * Fila de agente MIA por cuenta conectada, renderizada debajo de su plataforma en
 * `PlatformsOptions`. `agentOptions`/`loadOptions` vienen ya resueltos del padre
 * (`PlatformsModal`): un solo fetch de agentes compartido por todas las filas, no uno
 * por select — `AsyncPaginatedSelectMC` no soporta paginar bien en este uso.
 */
const PlatformAgentAccounts = ({
  accounts,
  loadOptions,
  pendingValues,
  onSelect,
  isAgentDeleted
}) => {
  const { t } = useTranslation('ChatbotIA');

  if (accounts.length === 0) return null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-mt-2 tw-mb-1 tw-pl-4 tw-border-l-2 tw-border-gray-100">
      {accounts.map((account) => {
        const key = buildAccountKey(account.platform, account);
        const currentValue = key in pendingValues ? pendingValues[key] : (account.mia_agent_id || '');
        const accountName = account.name || account.phone_number || account.external_id;
        const accountId = account.external_id || account.id;

        return (
          <div key={key} className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-flex-1 tw-min-w-0">
              <div className="tw-font-medium tw-text-gray-900 tw-text-xs tw-truncate">{accountName}</div>
              <div className="tw-text-gray-500 tw-text-2xs tw-truncate">{accountId}</div>
              {isAgentDeleted(account) && (
                <div className="tw-text-[10px] tw-text-orange-500">
                  {t('config.platforms.agent_accounts.agent_deleted')}
                </div>
              )}
            </div>

            <div className="tw-flex-shrink-0 tw-w-56">
              <AsyncPaginatedSelectMC
                loadOptions={loadOptions}
                onSelect={(value) => onSelect(key, value)}
                defaultValue={currentValue}
                // `defaultValue=''` es falsy: AsyncPaginatedSelectMC nunca resuelve su
                // label (solo lo intenta cuando `defaultValue` es truthy), así que sin
                // esto se ve el placeholder genérico en vez de la opción "Agente
                // predeterminado" realmente seleccionada.
                placeholder={t('config.platforms.agent_accounts.default_agent')}
                search={false}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlatformAgentAccounts;
